import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { useToast } from '@/components/ui/hooks/use-toast';
import { comboBoxOptionsOf, requiredSelectOption } from '@/components/ui/multi-select-utils';
import { createOrg, invalidateOrgQueries } from '@/lib/api-calls';
import { getTimezoneTypeahead } from '@/lib/time-zones';
import {
  Org,
  OrgFeedbackTypeLabels,
  orgFeedbackTypeSchema,
  VideoConferenceTypeLabels,
  videoConferenceTypeSchema,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { FormInputDate } from '@/components/forms/form-input-date';
import { Temporal } from 'temporal-polyfill';

const formSchema = z
  .object({
    name: z.string(),
    timeZone: requiredSelectOption('Select a time zone'),
    edlinkId: z.string().optional(),
    videoConferenceType: videoConferenceTypeSchema,
    feedbackType: orgFeedbackTypeSchema,
    preliminaryStartPlainDate: z.instanceof(Temporal.PlainDate).optional(),
    preliminaryEndPlainDate: z.instanceof(Temporal.PlainDate).optional(),
    totalSeats: z.coerce
      .number({ message: 'Please enter a valid number.' })
      .int({ message: 'Please enter a integer number.' })
      .min(0)
      .default(0),
  })
  .refine(
    (data) => {
      if (data.preliminaryStartPlainDate && data.preliminaryEndPlainDate) {
        return Temporal.PlainDate.compare(data.preliminaryStartPlainDate, data.preliminaryEndPlainDate) <= 0;
      }
      return true;
    },
    {
      message: 'End date must be after start date',
      path: ['end'],
    },
  );

type CreatedOrgProps = {
  onCreated?: (org: Org) => void;
  trigger: React.ReactNode;
};

export const CreateOrg = (props: CreatedOrgProps) => {
  const { onCreated, trigger } = props;

  const { mutateAsync } = useMutation({
    mutationFn: createOrg,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    try {
      const start = formData.preliminaryStartPlainDate
        ? Temporal.PlainDate.from(formData.preliminaryStartPlainDate ?? '').toString()
        : undefined;
      const end = formData.preliminaryEndPlainDate
        ? Temporal.PlainDate.from(formData.preliminaryEndPlainDate ?? '').toString()
        : undefined;

      const newOrg = await mutateAsync({
        start,
        end,
        timeZone: formData.timeZone.value,
        edlinkId: formData.edlinkId?.trim() || undefined,
        name: formData.name.trim(),
        videoConferenceType: formData.videoConferenceType,
        feedbackType: formData.feedbackType,
        totalSeats: formData.totalSeats,
        status: 'active',
      });
      toast({
        title: 'District created successfully',
      });

      onCreated?.(newOrg);

      closeModal();
      invalidateOrgQueries(queryClient);
    } catch {
      toast({
        title: 'District creation failed',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Create District"
      onSubmit={onSubmit}
      formSchema={formSchema}
      trigger={trigger}
      confirmBtnLabel="Create"
      defaultValues={{
        videoConferenceType: 'zoom',
        feedbackType: 'airFeedback',
        preliminaryStartPlainDate: undefined,
        preliminaryEndPlainDate: undefined,
        totalSeats: 0,
      }}
    >
      {({ form }) => {
        return (
          <>
            <div className="grid grid-cols-2 gap-4 py-4">
              <FormInputField
                name="name"
                label="District Name"
                form={form}
                inputProps={{ required: true, className: 'w-full' }}
              />
              <FormComboBox
                name="timeZone"
                label="Time zone"
                form={form}
                {...comboBoxOptionsOf(getTimezoneTypeahead)}
                triggerProps={{ className: 'w-full' }}
                required
              />
              <FormInputField
                name="edlinkId"
                label="Edlink ID"
                form={form}
                inputProps={{ className: 'font-mono w-full' }}
                className="col-span-2"
              />
              <FormSingleSelect
                name="feedbackType"
                label="Feedback Type"
                form={form}
                options={orgFeedbackTypeSchema.options}
                renderOption={(type) => OrgFeedbackTypeLabels[type]}
                selectProps={{
                  required: true,
                }}
              />
              <FormSingleSelect
                name="videoConferenceType"
                label="Meeting Type"
                form={form}
                options={videoConferenceTypeSchema.options}
                renderOption={(type) => VideoConferenceTypeLabels[type]}
                selectProps={{
                  required: true,
                }}
              />
              <FormInputDate
                name="preliminaryStartPlainDate"
                label="Preliminary start date"
                form={form}
                triggerButtonProps={{ className: 'w-full' }}
              />
              <FormInputDate
                name="preliminaryEndPlainDate"
                label="Preliminary end date"
                form={form}
                triggerButtonProps={{ className: 'w-full' }}
              />
              <FormInputField name="totalSeats" label="Total seats" form={form} inputProps={{ className: 'w-full' }} />
            </div>
          </>
        );
      }}
    </FormModal>
  );
};
